// What the New Relic browser agent is started with, and which file downloads it times. Kept apart from the agent itself,
// which reads the page as it loads, so both can be checked without a browser.
import { isThumbnail } from '@/utils/storageAddress';

export interface NrBrowserConfig {
  licenseKey: string;
  applicationID: string;
  environment: string;
}

/**
 * The agent's `init`: the backend's calls traced across origins, so each call's browser timing joins the backend's own
 * span for it, which carries the region that answered (fly.region) and the one the user reached Fly in. The agent's own
 * resource timings stay off: they would time every file the page loads (its code, every API call, every thumbnail).
 * The asset downloads the region view needs are timed one by one instead (assetDownload).
 */
export function agentInit(backendUrl: string) {
  const backend = new URL(backendUrl);
  return {
    // The backend is another origin: the agent adds its trace headers only to the origins named here
    distributed_tracing: {
      enabled: true,
      allowed_origins: [backend.origin],
      cors_use_newrelic_header: true,
      cors_use_tracecontext_headers: true,
    },
    performance: {
      resources: { enabled: false, asset_types: [], first_party_domains: [], ignore_newrelic: true },
    },
    privacy: { cookies_enabled: true },
  };
}

/** Where the signed-in user's id goes: the agent's setUserId, which puts it on every later event as enduser.id. */
export interface UserIdSink {
  setUserId(value: string | null): unknown;
}

const UNIQUE_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The id the agent is given for a user: their unique id (a UUID) and nothing else, never an email or a name. */
export function endUserId(id: string | null | undefined): string | null {
  return typeof id === 'string' && UNIQUE_ID.test(id) ? id.toLowerCase() : null;
}

/**
 * Keeps the agent's user id in step with who is signed in, so the admin's Deploy page can say who a place's web sessions
 * are. The app may learn the user before the agent starts (it waits for analytics consent): the id is kept and handed
 * over when it does. Signing out clears it.
 */
export function createEndUser() {
  let sink: UserIdSink | null = null;
  let current: string | null = null;
  return {
    attach(agent: UserIdSink): void {
      sink = agent;
      if (current) agent.setUserId(current);
    },
    set(id: string | null | undefined): void {
      const next = endUserId(id);
      if (next === current) return;
      current = next;
      sink?.setUserId(next);
    },
    /** The id the agent has, or will be handed as it starts. */
    current: () => current,
  };
}

/** The custom attribute naming the device the app sends the backend (X-Lureo-Device), as the backend's spans name it. */
export const DEVICE_ATTRIBUTE = 'lureo.device.id';

/** Where the device id goes: the agent's setCustomAttribute, kept for the agent's session so a reload starts with it. */
export interface CustomAttributeSink {
  setCustomAttribute(name: string, value: string | null, persist?: boolean): unknown;
}

/**
 * Keeps the agent's lureo.device.id in step with the device id the app sends the backend, so the admin's people read can
 * join a browser session to the backend's device rows by field. As with the user id, an id learnt before the agent
 * starts is handed over when it does, and one that goes (PostHog turned off) is cleared.
 */
export function createDeviceAttribute() {
  let sink: CustomAttributeSink | null = null;
  let current: string | null = null;
  return {
    attach(agent: CustomAttributeSink): void {
      sink = agent;
      if (current) agent.setCustomAttribute(DEVICE_ATTRIBUTE, current, true);
    },
    set(id: string | null | undefined): void {
      const next = typeof id === 'string' && id.length > 0 ? id : null;
      if (next === current) return;
      current = next;
      sink?.setCustomAttribute(DEVICE_ATTRIBUTE, next, true);
    },
  };
}

/** The hosts the CDN serves asset and model files from: assets.lureo.com, and each environment's assets.<env>.lureo.com. */
const ASSETS_HOST = /^assets\.(?:[a-z0-9-]+\.)?lureo\.com$/i;

/** Downloads code makes (a model loader, a file it reads), not an image an element shows. */
const DOWNLOADED_BY_CODE = new Set(['fetch', 'xmlhttprequest']);

/** The fields of a PerformanceResourceTiming entry this reads. */
export interface ResourceTimingEntry {
  name: string;
  initiatorType: string;
  startTime: number;
  duration: number;
  responseEnd?: number;
  transferSize?: number;
  encodedBodySize?: number;
  decodedBodySize?: number;
  nextHopProtocol?: string;
  responseStatus?: number;
}

/** One asset download as the agent's measure records it: a BrowserPerformance event named by the file's address. */
export interface AssetDownload {
  name: string;
  start: number;
  end: number;
  customAttributes: Record<string, string | number>;
}

/**
 * An asset or model download from the assets domains, as the agent's measure takes it; null for any other resource (the
 * app's code, the backend's calls, thumbnails and other images, files from anywhere else). The address loses its query.
 */
export function assetDownload(entry: ResourceTimingEntry): AssetDownload | null {
  let url: URL;
  try {
    url = new URL(entry.name);
  } catch {
    return null;
  }
  // An image's thumbnail (`<path>.w1152.jpg`, made at upload beside it) is a card's picture, not an asset download
  if (!ASSETS_HOST.test(url.hostname) || !DOWNLOADED_BY_CODE.has(entry.initiatorType) || isThumbnail(url.pathname)) return null;
  const end = entry.responseEnd && entry.responseEnd >= entry.startTime ? entry.responseEnd : entry.startTime + entry.duration;
  const customAttributes: Record<string, string | number> = { initiatorType: entry.initiatorType, assetHost: url.hostname };
  for (const field of ['transferSize', 'encodedBodySize', 'decodedBodySize', 'responseStatus'] as const) {
    const value = entry[field];
    if (typeof value === 'number') customAttributes[field] = value;
  }
  if (entry.nextHopProtocol) customAttributes.nextHopProtocol = entry.nextHopProtocol;
  return { name: `${url.origin}${url.pathname}`, start: entry.startTime, end, customAttributes };
}

/** Where the asset downloads are recorded: the agent's measure. */
export interface MeasureSink {
  measure(name: string, options: { start: number; end: number; customAttributes: Record<string, string | number> }): unknown;
}

/** Records each asset download as it finishes (and those finished before the agent started), one measure each. */
export function recordAssetDownloads(agent: MeasureSink, entries: (each: (entry: ResourceTimingEntry) => void) => void): void {
  entries(entry => {
    const download = assetDownload(entry);
    if (download) agent.measure(download.name, { start: download.start, end: download.end, customAttributes: download.customAttributes });
  });
}
